import {
  Component,
  Host,
  h,
  Element,
  Prop,
  Watch,
  Event,
  EventEmitter,
  State,
} from '@stencil/core';
import { AhaTree } from '../aha-tree/aha-tree';

export interface AhaTreeItem extends HTMLAhaTreeItemElement {}

@Component({
  tag: 'aha-tree-item',
  styleUrl: 'aha-tree-item.css',
  shadow: true,
})
export class AhaTreeItem {
  @Element() host!: HTMLAhaTreeItemElement;

  private defaultSlot?: HTMLSlotElement;

  @Prop({ reflect: true }) ariaExpanded!: string;

  @Prop({ reflect: true }) ariaLevel!: string;

  @Prop({ reflect: true }) ariaSelected: string = 'false';
  @Watch('ariaSelected')
  ariaSelectedChanged(value: string = this.ariaSelected) {
    if (value === 'true') {
      this.host.tabIndex = 0;
      this.hidden = false;
      this.selectEvent.emit({ item: this.host });
    } else {
      this.host.tabIndex = -1;
    }
    if (value === 'true' && this.hasChildren()) this.expanded = this.selected;
  }

  get expanded() {
    return this.ariaExpanded === 'true';
  }
  set expanded(value: boolean) {
    if (this.hasChildren()) {
      this.ariaExpanded = value ? 'true' : 'false';
    } else {
      this.host.removeAttribute('aria-expanded');
    }
  }

  get hidden() {
    return this.host.ariaHidden === 'true';
  }

  set hidden(value: boolean) {
    this.host.ariaHidden = value ? 'true' : 'false';
  }

  get level() {
    return Number(this.ariaLevel);
  }
  set level(value: number) {
    this.ariaLevel = Math.max(1, value).toString();
  }

  get selected() {
    return this.ariaSelected === 'true';
  }
  set selected(value: boolean) {
    this.ariaSelected = value ? 'true' : 'false';
  }

  get tree() {
    return this.host.closest<AhaTree>('aha-tree');
  }

  // Whether the tree item has children.
  // Can be passed a list of items, otherwise will default to fetching the parent tree's items.
  // If you are doing a lot of work with the tree, it is more efficient to pass the tree's items as an argument here.
  hasChildren(items?: HTMLAhaTreeItemElement[]) {
    if (!items) {
      const tree = this.tree;
      if (!tree) return false;
      items = tree.items;
    }
    const index = items.indexOf(this.host);
    const nextItem = items[index + 1];
    if (nextItem instanceof AhaTreeItem) return nextItem.level > this.level;
    return false;
  }

  @State() showCollapser = false;

  // A `childLink` is the immediate child of the aha-tree-item's default slot, when that child is an anchor element.
  // This is used to forward a click event when the user hits "Enter" on the tree item.
  get childLink() {
    const assignedElements = this.defaultSlot?.assignedElements();
    return assignedElements &&
      assignedElements.length === 1 &&
      assignedElements[0] instanceof HTMLAnchorElement
      ? assignedElements[0]
      : null;
  }

  handleClick = (e: MouseEvent) => {
    if (e.defaultPrevented) return;
    this.selected = true;

    // If the tree item has a child link, we want to click it when the tree item is clicked.
    const childLink = this.childLink;
    const target = e.target as HTMLElement;
    if (target && childLink) {
      // If the target is the <a> link itself, then we don't want to click it again.
      if (target.closest('a') !== childLink) childLink.click();
    }
  };

  // Collapse the tree item if user clicks on the embedded collapse button.
  handleCollapserClick = (e: MouseEvent) => {
    this.ariaExpanded = this.ariaExpanded === 'true' ? 'false' : 'true';
    e.stopImmediatePropagation();
  };

  connectedCallback() {
    this.ariaSelectedChanged();
  }

  componentDidLoad() {
    // Set a default level if none is specified.
    if (!this.ariaLevel) this.level = 1;
  }

  @Event({
    eventName: 'select',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  selectEvent!: EventEmitter;

  // Hide the icon slot if it is empty.
  private handleIconSlotChanged = (e: Event) => {
    const slot = e.target as HTMLSlotElement;
    const assignedElements = slot.assignedElements();
    if (slot.parentElement) {
      slot.parentElement.ariaHidden = assignedElements.length ? 'false' : 'true';
    }
  };

  render() {
    return (
      <Host
        role="treeitem"
        onClick={this.handleClick}
        style={{ '--aria-level': this.level.toString() }}
      >
        {this.ariaExpanded && (
          <div part="group-icon" id="group-icon" onClick={this.handleCollapserClick}>
            <slot name="group-icon">
              <fa-icon
                id="default-icon"
                part="default-icon"
                class="fa-regular fa-angle-right"
              ></fa-icon>
            </slot>
          </div>
        )}
        <div id="wrapper">
          <div id="content" part="content">
            <div id="icon" part="icon" aria-hidden="true">
              <slot name="icon" onSlotchange={this.handleIconSlotChanged}></slot>
            </div>
            <slot ref={e => (this.defaultSlot = e as HTMLSlotElement)}></slot>
          </div>
        </div>
      </Host>
    );
  }
}
